import type { AnyClock } from "@repo/lib/clock";
import { ObjectKeyHasher } from "@repo/lib/keyHasher";
import {
  RepoCache,
  type GetObservableOptions,
} from "@repo/shared-client/repoCache";
import {
  CreateAgendaModuleDtoSchema,
  PatchAgendaModuleDtoSchema,
  type AgendaModule,
} from "@repo/types";
import {
  cacheDefaults,
  hookPriorities,
  messageServiceSubscribe,
  SubscriptionMap,
} from "./common";
import { apiService } from "@/services/apiService";
import { of, type Observable } from "rxjs";
import { nullObservable } from "@repo/shared-client/commonObservables";
import type { OnlyRequire } from "@repo/lib/types";
import { agendaItemRepo, type AgendaItemDto } from "./agendaItemRepo";
import { RepoMutationHooks } from "./repoMutationHooks";
import { baseRepo } from "./baseRepo";

type AgendaModuleGetKey = {
  type: "get";
  tenantId: string;
  agendaModuleId: string;
};
type AgendaModuleListKey = { type: "list"; tenantId: string };
type AgendaModuleKey = AgendaModuleGetKey | AgendaModuleListKey;

export type AgendaModuleDto = AgendaModule & {
  tenantId: string;
  agendaItems: AgendaItemDto[];
};

export type AgendaModuleIdPath = {
  tenantId: string;
  agendaModuleId: string | null;
};

export class AgendaModuleRepository {
  private cache: RepoCache<string, AgendaModuleDto, ObjectKeyHasher, AnyClock>;
  private hooks = new RepoMutationHooks<AgendaModuleIdPath, AgendaModuleDto>();
  private subscribeMap = new SubscriptionMap();

  // expose hook registry
  addHook = this.hooks.addHook;
  removeHook = this.hooks.removeHook;

  publishNewer(agendaModule: AgendaModuleDto) {
    this.cache.publish(agendaModule.id, agendaModule);
  }

  agendaModuleToDto(
    tenantId: string,
    agendaModule: AgendaModule
  ): AgendaModuleDto;
  agendaModuleToDto(tenantId: string, agendaModule?: null): null;
  agendaModuleToDto(
    tenantId: string,
    agendaModule?: AgendaModule | null
  ): AgendaModuleDto | null;
  agendaModuleToDto(
    tenantId: string,
    agendaModule?: AgendaModule | null
  ): AgendaModuleDto | null {
    const agendaItems =
      agendaModule?.agendaItems?.map((agendaItem) =>
        agendaItemRepo.agendaItemToDto(tenantId, agendaItem)
      ) ?? [];
    return agendaModule ? { ...agendaModule, tenantId, agendaItems } : null;
  }

  constructor() {
    this.cache = new RepoCache({
      requestKeyHasher: new ObjectKeyHasher({ sortKeys: false }),
      expiresAfterSeconds: cacheDefaults.expiresAfterSeconds,
      purgeExpirationAfterSeconds: cacheDefaults.purgeExpiredAfterSeconds,
      fetch: async ({ requestKey, publish }) => {
        const key = requestKey as AgendaModuleKey;

        switch (key.type) {
          case "get": {
            const { tenantId, agendaModuleId } = key;
            const agendaModule = (
              await apiService.v1.getAgendaModule(tenantId, agendaModuleId)
            ).data;

            // only publish if it's the right tenant
            const prev = this.cache.table.get(agendaModuleId);
            if (!prev || prev.tenantId === tenantId) {
              publish(
                agendaModuleId,
                this.agendaModuleToDto(tenantId, agendaModule)
              );
            }
            break;
          }
          case "list": {
            const { tenantId } = key;
            const agendaModules = (
              await apiService.v1.listAgendaModules(tenantId)
            ).data;
            for (const agendaModule of agendaModules) {
              publish(
                agendaModule.id,
                this.agendaModuleToDto(tenantId, agendaModule)
              );
            }
            break;
          }
        }
      },
      collect: ({ requestKey, table }) => {
        const key = requestKey as AgendaModuleKey;

        switch (key.type) {
          case "get": {
            return table.get(key.agendaModuleId) ?? null;
          }
          case "list": {
            const agendaModules: AgendaModuleDto[] = [];
            for (const agendaModule of table.values()) {
              if (agendaModule.tenantId === key.tenantId) {
                agendaModules.push(agendaModule);
              }
            }
            return agendaModules;
          }
        }
      },
      affectedKeys: ({ entryData, affectsKey }) => {
        // extract
        const { tenantId, id } = entryData;
        // affect
        affectsKey(this.getKey(tenantId, id));
        affectsKey(this.listKey(tenantId));
      },
      initialSubscribe: ({ strKey, requestKey }) => {
        const key = requestKey as AgendaModuleKey;
        if (key.type !== "get" && key.type !== "list") return;

        const subscription = messageServiceSubscribe(
          "changing.agendaModules",
          key.type === "get" ? [key.agendaModuleId] : [],
          () => this.cache.fetch(key)
        );

        this.subscribeMap.addSubscription(strKey, subscription);
      },
      subscriptionPublished: ({ strKey, requestKey, collectedData }) => {
        const key = requestKey as AgendaModuleKey;

        switch (key.type) {
          case "get": {
            const data = collectedData as AgendaModuleDto | null;
            if (!data) {
              this.subscribeMap.unsubscribe(strKey);
              break;
            }

            this.subscribeMap.updateChildren({
              strKey,
              idArray: (data.agendaItemIds ??= []),
              objectArray: (data.agendaItems ??= []),
              subscribeChild: (id) =>
                agendaItemRepo.get(key.tenantId, id, { initialFetch: false }),
              publishParent: (idArray, objectArray) => {
                data.agendaItemIds = idArray;
                data.agendaItems = objectArray;
                this.cache.publish(key.agendaModuleId, data);
              },
            });
            break;
          }
        }
      },
      finalUnsubscribe: ({ strKey }) => {
        this.subscribeMap.unsubscribe(strKey);
      },
    });

    baseRepo.addRepo(this);
  }

  private getKey(tenantId: string, agendaModuleId: string): AgendaModuleGetKey {
    return { type: "get", tenantId, agendaModuleId };
  }
  private listKey(tenantId: string): AgendaModuleListKey {
    return { type: "list", tenantId };
  }

  get(
    tenantId: string,
    agendaModuleId: string,
    options?: GetObservableOptions
  ): Observable<AgendaModuleDto | null> {
    if (!tenantId || !agendaModuleId) {
      return nullObservable;
    }
    const key = this.getKey(tenantId, agendaModuleId);
    return this.cache.observable(key, options);
  }
  getCached(tenantId: string, agendaModuleId: string): AgendaModuleDto | null {
    const key = this.getKey(tenantId, agendaModuleId);
    return this.cache.collectIfUnexpired(key, null);
  }

  list(
    tenantId: string,
    options?: GetObservableOptions
  ): Observable<AgendaModuleDto[]> {
    if (!tenantId) {
      return of([]);
    }
    const key = this.listKey(tenantId);
    return this.cache.observable(key, options);
  }
  listCached(tenantId: string): AgendaModuleDto[] | null {
    const key = this.listKey(tenantId);
    return this.cache.collectIfUnexpired(key, null);
  }

  async create(
    tenantId: string,
    data: Omit<Partial<AgendaModuleDto>, "agendaItems">
  ): Promise<AgendaModuleDto> {
    if (!tenantId) {
      throw new Error("Failed to create agenda module: tenantId is blank.");
    }

    const hooks = this.hooks.execute(
      { tenantId, agendaModuleId: null },
      "create"
    );
    data = await hooks.before(data);

    let created = this.agendaModuleToDto(
      tenantId,
      (
        await apiService.v1.createAgendaModule(
          tenantId,
          CreateAgendaModuleDtoSchema.parse(data)
        )
      ).data
    );

    created = await hooks.after(created);

    // publish new agenda module
    this.cache.publish(created.id, created);

    return created;
  }

  async update(
    tenantId: string,
    agendaModuleId: string,
    data: Omit<Partial<AgendaModuleDto>, "agendaItems">
  ): Promise<AgendaModuleDto> {
    if (!tenantId || !agendaModuleId) {
      throw new Error(
        "Failed to update agenda module: tenantId or agendaModuleId is blank."
      );
    }

    const hooks = this.hooks.execute({ tenantId, agendaModuleId }, "update");
    data = await hooks.before(data);

    let updated = this.agendaModuleToDto(
      tenantId,
      (
        await apiService.v1.updateAgendaModule(
          tenantId,
          agendaModuleId,
          PatchAgendaModuleDtoSchema.parse(data)
        )
      ).data
    );

    updated = await hooks.after(updated);

    // publish updated agenda module
    this.cache.publish(updated.id, updated);

    return updated;
  }

  async delete(tenantId: string, agendaModuleId: string): Promise<void> {
    if (!tenantId || !agendaModuleId) {
      throw new Error(
        "Failed to delete agenda module: tenantId or agendaModuleId is blank."
      );
    }

    const hooks = this.hooks.execute({ tenantId, agendaModuleId }, "delete");
    await hooks.before(null);

    await apiService.v1.deleteAgendaModule(tenantId, agendaModuleId);

    await hooks.after(null);
    this.cache.publish(agendaModuleId, null);
  }

  invalidate(agendaModule: OnlyRequire<AgendaModuleDto, "id">) {
    this.cache.invalidateEntry(agendaModule.id);
  }

  invalidateAll() {
    this.cache.clear();
  }

  init(data?: Partial<AgendaModuleDto>): AgendaModuleDto {
    return baseRepo.init({
      tenantId: baseRepo.getTenantId() || "UNKNOWN",
      status: "draft",
      name: "",
      description: "",
      body: "",
      type: "",
      agendaItemIds: [],
      agendaItems: [],
      ...data,
    });
  }
}

export const agendaModuleRepo = new AgendaModuleRepository();

agendaModuleRepo.addHook(() => {
  const before = (agendaModule: Partial<AgendaModuleDto>) => {
    return { ...agendaModule, agendaItems: undefined };
  };

  return {
    beforeCreate: before,
    beforeUpdate: before,
  };
}, hookPriorities.removeNestedEntities);
